import { useMemo, useCallback, useEffect, useRef } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Tooltip } from "@fluentui/react-components";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import {
  ProjectIdCell,
  PowerBankerEmptyState,
  PowerBankerNoDealOnFilterState,
  PowerBankerNoDealOnSearchState,
  renderCellContent,
  getAccordionRowRender,
  applySearchFilter,
  getFilteredSortedData,
  useGroupToggle,
  makeDecimalCellHelper,
} from "./power-banker-helper";
import {
  renderTooltipHeader,
  createColumnMenu,
renderlastUpdatedTooltipHeader,
formatLastUpdatedDate,
highlightMatch,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import {
  getLastUpdatedDateColorWeeks,

} from '../../../dashboard-util/dashboard-utils'
import { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import PropTypes from "prop-types";
import "./power-banker-time-log.scss";

const PowerBankerDetailGrid = ({
  data = [],
  onItemChange,
  canCreate,
  canEdit,
  detailState,
  onDetailStateChange,
  searchValue,
  onVisibleDataChange,
  resetDataState,
  refineSearch
}) => {
  const hasMounted = useRef(false);
  const { sort, filter } = detailState;
  const { groupState, toggleGroup, setGroupState } = useGroupToggle();
  const filteredData = useMemo(() => getFilteredSortedData(data, filter, sort), [data, filter, sort]);
  const visibleData = useMemo(() => filteredData, [filteredData]);
  const visibleLength = useMemo(() => visibleData.length, [visibleData.length]);

  useEffect(() => {
    if (hasMounted.current) {
      applySearchFilter(searchValue, detailState, onDetailStateChange, setGroupState);
    } else {
      hasMounted.current = true;
    }
  }, [searchValue]);

   useEffect(() => {
    if (onVisibleDataChange) {
      onVisibleDataChange({
        isEmpty: visibleData.length === 0,
        searchValue,
      });
    }
  }, [visibleData, searchValue, onVisibleDataChange]);

  const columnMenu = useMemo(() => createColumnMenu(data, searchValue), [data, searchValue]);
  const columnIsFiltered = useCallback(
    (fieldName) => isFilteredColumn(fieldName, filter),
    [filter]
  );
  const AccordionRowRender = useMemo(() => getAccordionRowRender(filteredData, groupState, toggleGroup,7), [filteredData, groupState]);
  const isEmpty = visibleData.length === 0;

  const finalGridData = isEmpty
    ? [{
        isEmptyRow: true,
        isSearchEmptyState: searchValue?.trim() !== "",
        isFilterEmptyState: searchValue?.trim() === "" && (filter?.filters?.length ?? 0) > 0,
      }]
    : visibleData;

  const makeDecimalCell = useMemo(() => makeDecimalCellHelper(visibleLength),[visibleLength] );
  const LastWeekCell = useMemo(() => makeDecimalCell("LastWeekHours"),[makeDecimalCell]);
  const WeekendCell = useMemo(() => makeDecimalCell("WeekendHours"),[makeDecimalCell]);
  const NextWeekCell = useMemo(() => makeDecimalCell("NextWeekHours"),[makeDecimalCell]);

  return (
    <div className="detail-grid-scroll-container">
      <Grid
        data={finalGridData}
        rowRender={AccordionRowRender}
        sortable={true}
        sort={detailState.sort}
        filter={detailState.filter}
        onSortChange={(e) => onDetailStateChange({ ...detailState, sort: e.sort })}
        onFilterChange={(e) =>
          onDetailStateChange({ ...detailState, filter: e.filter })
        }
        editable="true"
        editField="inEdit"
        onItemChange={onItemChange}
        navigable={true}
        keyboardNavigation={true}
        dataItemKey="ProjectId"
        resizable={true}
      >
        <Column
          field="ProjectId"
          editable={false}
          title={renderTooltipHeader("#")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("ProjectId") ? "filtered" : ""}
          cell={(props) =>
            props.dataItem.isEmptyRow ? (
              <td colSpan={8} className="text-center k-table-td">
                {props.dataItem.isSearchEmptyState ? (
                  <PowerBankerNoDealOnSearchState
                    resetDataState={resetDataState}
                    refineSearch={refineSearch}
                  />
                ) : props.dataItem.isFilterEmptyState ? (
                  <PowerBankerNoDealOnFilterState resetDataState={resetDataState} />
                ) : (
                  <PowerBankerEmptyState canCreate={canCreate} />
                )}
              </td>
            ) : (
              <td className="background-white k-table-td">
                <ProjectIdCell
                  {...props}
                  canEdit={canEdit}
                  isNewDeal={props.dataItem.IsNew}
                />
              </td>
            )
          }
        />
        <Column
            field="ClientName"
            title={renderTooltipHeader("Client Name")}
            editable={false}
            filter="text"
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("ClientName") ? "filtered" : ""}
            cell={(props) =>
            renderCellContent(visibleData.length, null, (
                <td className="background-white k-table-td">
                <Tooltip content={props.dataItem.ClientName} relationship="label">
                    <span>{highlightMatch(props.dataItem.ClientName, searchValue)}</span>
                </Tooltip>
                </td>
            ))
            }
        />
        <Column
          field="ProjectName"
          title={renderTooltipHeader("Project Name")}
          editable={false}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("ProjectName") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td">
                <Tooltip content={props.dataItem.ProjectName} relationship="label">
                  <div className="project-name-wrapper">
                    <span className="project-name-text">
                      {highlightMatch(props.dataItem.ProjectName, searchValue)}
                    </span>
                    {props.dataItem.IsOverdue && (
                    <FluentBadge
                      appearance="filled"
                      size="small"
                      color="warning"
                      text="Overdue"
                    />
                  )}
                  </div>
                </Tooltip>
              </td>
            ))
          }
        />
        <Column
          field="LastWeekHours"
          title={renderTooltipHeader("Hrs Spent Last Wk")}
          filter="numeric"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("LastWeekHours") ? "filtered" : ""}
          cell={LastWeekCell}
        />
        <Column
          field="WeekendHours"
          title={renderTooltipHeader("Wknd Hrs Spent")}
          filter="numeric"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("WeekendHours") ? "filtered" : ""}
          cell={ WeekendCell }
        />
        <Column
          field="NextWeekHours"
          title={renderTooltipHeader("Est Hrs for Next Wk")}
          filter="numeric"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("NextWeekHours") ? "filtered" : ""}
          cell={ NextWeekCell }
        />
        <Column
            field="LastUpdated"
            title={renderlastUpdatedTooltipHeader("Last Updated")}
            columnMenu={columnMenu}
            filter="date"
            headerClassName={columnIsFiltered("LastUpdated") ? "filtered" : ""}
            cell={(props) => (
              renderCellContent(visibleData.length, null, (
                <td className="background-white k-table-td" style={{ color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdated) }}>
                    <span>{formatLastUpdatedDate(props.dataItem.LastUpdated) ?? "—"}</span>
                </td>
              ))
            )}
        />
      </Grid>
    </div>
  );
};

export default PowerBankerDetailGrid;

PowerBankerDetailGrid.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      GroupName: PropTypes.string.isRequired,
      ProjectId: PropTypes.number.isRequired,
      ProjectIdString: PropTypes.string.isRequired,
      TaskId: PropTypes.number.isRequired,
      ProjectName: PropTypes.string.isRequired,
      ClientName: PropTypes.string,
      IsNew: PropTypes.bool,
      IsOverdue: PropTypes.bool,
      LastUpdated: PropTypes.string,
      LastUpdatedString: PropTypes.string,
      LastWeekHours: PropTypes.number,
      WeekendHours: PropTypes.number,
      NextWeekHours: PropTypes.number,
    })
  ).isRequired,
  onItemChange: PropTypes.func.isRequired,
  canCreate: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  searchValue: PropTypes.string,
  onVisibleDataChange: PropTypes.func,
  resetDataState: PropTypes.func.isRequired,
  detailState: PropTypes.shape({
    sort: PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string,
        dir: PropTypes.oneOf(["asc", "desc"]),
      })
    ),
    filter: PropTypes.object,
  }).isRequired,
  onDetailStateChange: PropTypes.func.isRequired,
  refineSearch: PropTypes.func.isRequired,
};